import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Avatar,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Collapse,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Route as OptimizedIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { orderService } from "../../services/orderService";
import { routeService } from "../../services/routeService";
import { userService } from "../../services/userService";

const RouteEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [route, setRoute] = useState(null);
  const [orders, setOrders] = useState([]);
  const [routeName, setRouteName] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [medicalProfessionals, setMedicalProfessionals] = useState([]);

  // 🔥 NEW: Schedule editing states
  const [scheduleDate, setScheduleDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [scheduleExpanded, setScheduleExpanded] = useState(false);

  // State for order operations
  const [openAddOrderDialog, setOpenAddOrderDialog] = useState(false);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [orderDateFilter, setOrderDateFilter] = useState("");

  useEffect(() => {
    const loadMedicalProfessionals = async () => {
      try {
        const mpsResponse =
          await routeService.getMedicalProfessionalsWithStartingPoints();
        setMedicalProfessionals(mpsResponse || []);
      } catch (error) {
        console.error("Error loading medical professionals:", error);
        // Fallback to regular user service
        try {
          const fallbackResponse = await userService.getMedicalProfessionals();
          setMedicalProfessionals(fallbackResponse || []);
        } catch (fallbackError) {
          console.error("Fallback MP loading failed:", fallbackError);
        }
      }
    };

    fetchRouteData();
    loadMedicalProfessionals();
  }, [id]);

  const fetchRouteData = async () => {
    try {
      setLoading(true);
      const routeData = await routeService.getRouteById(id);
      setRoute(routeData);
      setRouteName(routeData.name);

      // 🔥 NEW: Set schedule values
      setScheduleDate(routeData.scheduled_date || "");
      setStartTime(routeData.scheduled_time_start || "08:00");
      setEndTime(routeData.scheduled_time_end || "17:00");

      const ordersData = await routeService.getRouteOrders(id);
      setOrders(ordersData || []);
    } catch (error) {
      console.error("Error fetching route data:", error);
      setError(`Error loading route: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 NEW: Validate schedule data
  const validateSchedule = () => {
    const errors = [];

    if (!scheduleDate) {
      errors.push("Schedule date is required");
    } else {
      const selectedDate = new Date(scheduleDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // To Do
      // if (selectedDate < today && route?.status !== "completed") {
      //   errors.push("Cannot schedule routes in the past");
      // }
    }

    if (startTime && endTime) {
      const [startHour, startMin] = startTime.split(":").map(Number);
      const [endHour, endMin] = endTime.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (startMinutes >= endMinutes) {
        errors.push("Start time must be before end time");
      }
    }

    return errors;
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate schedule
      const scheduleErrors = validateSchedule();
      if (scheduleErrors.length > 0) {
        setError(scheduleErrors.join(", "));
        return;
      }

      // Prepare order IDs in the new order
      const orderIds = orders.map((order) => order.id);

      // 🔥 NEW: Include schedule data in update
      const updateData = {
        name: routeName,
        scheduled_date: scheduleDate,
        scheduled_time_start: startTime,
        scheduled_time_end: endTime,
        order_ids: orderIds,
      };


      // Update route data
      await routeService.updateRoute(id, updateData);

      setSuccess("Route updated successfully");
      setTimeout(() => {
        navigate(`/routes/${id}`);
      }, 1500);
    } catch (error) {
      console.error("Error saving route:", error);
      setError(`Failed to save changes: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddOrder = async () => {
    if (!selectedOrder) return;

    try {
      // Check if order is already in the route
      if (orders.some((o) => o.id === selectedOrder.id)) {
        setError("This order is already in the route");
        return;
      }

      // Add order to route
      await routeService.addOrderToRoute(id, selectedOrder.id);

      // Refresh orders
      const updatedOrders = await routeService.getRouteOrders(id);
      setOrders(updatedOrders || []);

      // Close dialog and reset selection
      setOpenAddOrderDialog(false);
      setSelectedOrder(null);
      setSuccess("Order added to route");
    } catch (error) {
      console.error("Error adding order to route:", error);
      setError(`Failed to add order: ${error.message}`);
    }
  };

  const handleRemoveOrder = async (orderId) => {
    try {
      await routeService.removeOrderFromRoute(id, orderId);

      // Update local state by removing the order
      setOrders(orders.filter((order) => order.id !== orderId));
      setSuccess("Order removed from route");
    } catch (error) {
      console.error("Error removing order:", error);
      setError(`Failed to remove order: ${error.message}`);
    }
  };

  const fetchAvailableOrders = async () => {
    try {
      setLoading(true);

      // Get route date for filtering - use the current schedule date being edited
      const routeDate = scheduleDate || route?.scheduled_date;
      setOrderDateFilter(routeDate);

      // Fetch orders with the same date that aren't in any route yet
      const response = await orderService.getAllOrders({
        scheduled_date: routeDate,
        status: "created",
      });

      // Filter out orders that are already in this route
      const routeOrderIds = orders.map((o) => o.id);
      const filteredOrders = response.filter(
        (o) => !routeOrderIds.includes(o.id)
      );

      setAvailableOrders(filteredOrders);
    } catch (error) {
      console.error("Error fetching available orders:", error);
      setError("Failed to load available orders");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setOpenAddOrderDialog(true);
    fetchAvailableOrders();
  };

  const handleDragEnd = (result) => {
    // Dropped outside the list
    if (!result.destination) return;

    const reorderedItems = Array.from(orders);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    setOrders(reorderedItems);
  };

  const handleMoveOrder = (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === orders.length - 1)
    ) {
      return; // Can't move beyond bounds
    }

    const newOrders = [...orders];
    const temp = newOrders[index];
    newOrders[index] = newOrders[index + direction];
    newOrders[index + direction] = temp;

    setOrders(newOrders);
  };

  // Filter available orders by search query
  const filteredAvailableOrders = availableOrders.filter((order) => {
    const orderNumber = order.order_number || "";
    const patientName = order.patient
      ? `${order.patient.first_name || ""} ${order.patient.last_name || ""}`
      : "";
    const address = order.patient?.address || "";

    const searchLower = orderSearchQuery.toLowerCase();
    return (
      orderNumber.toLowerCase().includes(searchLower) ||
      patientName.toLowerCase().includes(searchLower) ||
      address.toLowerCase().includes(searchLower)
    );
  });

  // 🔥 NEW: Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return "Not set";

    // Handle time strings like "08:00:00" or "08:00"
    const timeParts = timeString.split(":");
    if (timeParts.length >= 2) {
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    return timeString;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    // Force local time interpretation to avoid timezone shifts
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString();
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!route) {
    return (
      <Box textAlign="center" my={5}>
        <Typography variant="h6">Route not found</Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/routes")}
          sx={{ mt: 2 }}
        >
          Back to Routes
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate(`/routes/${id}`)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">Edit Route</Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CancelIcon />}
            onClick={() => navigate(`/routes/${id}`)}
            sx={{ mr: 2 }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveChanges}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid  size={{ xs: 12, md: 4 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Route Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Route Name */}
            <Box mb={3}>
              <TextField
                fullWidth
                label="Route Name"
                value={routeName}
                onChange={(e) => setRouteName(e.target.value)}
                disabled={saving}
              />
            </Box>

            {/* 🔥 NEW: Schedule Section */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => setScheduleExpanded(!scheduleExpanded)}
                  sx={{ cursor: "pointer" }}
                >
                  <Box display="flex" alignItems="center">
                    <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Schedule</Typography>
                  </Box>
                  {scheduleExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>

                <Collapse in={scheduleExpanded}>
                  <Box mt={2}>
                    {/* Current Schedule Display */}
                    <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                      >
                        Current Schedule
                      </Typography>
                      <Typography variant="body1">
                        📅 {formatDate(route.scheduled_date)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        ⏰ {formatTime(route.scheduled_time_start)} -{" "}
                        {formatTime(route.scheduled_time_end)}
                      </Typography>
                    </Box>

                    {/* Schedule Date */}
                    <TextField
                      fullWidth
                      label="Scheduled Date"
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                      sx={{ mb: 2 }}
                      helperText={
                        scheduleDate !== route.scheduled_date
                          ? "⚠️ Changing the date may affect order availability"
                          : ""
                      }
                    />

                    {/* Start Time */}
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                      sx={{ mb: 2 }}
                    />

                    {/* End Time */}
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={saving}
                      sx={{ mb: 2 }}
                    />

                    {/* Schedule Validation */}
                    {(() => {
                      const errors = validateSchedule();
                      return (
                        errors.length > 0 && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              {errors.join(", ")}
                            </Typography>
                          </Alert>
                        )
                      );
                    })()}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Route Status */}
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Status
              </Typography>
              <Chip
                label={route.status.replace("_", " ")}
                color={
                  route.status === "completed"
                    ? "success"
                    : route.status === "in_progress"
                    ? "primary"
                    : route.status === "assigned"
                    ? "info"
                    : "warning"
                }
                size="small"
              />
            </Box>

            {/* Medical Professional Starting Point Status */}
            {route.medical_professional_id && (
              <Box mb={3}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Medical Professional
                </Typography>
                {(() => {
                  const mp = medicalProfessionals.find(
                    (mp) => mp.id === route.medical_professional_id
                  );
                  return mp ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        src={mp.user?.avatar_url}
                        sx={{ width: 24, height: 24, bgcolor: "primary.main" }}
                      >
                        {mp.user?.full_name?.charAt(0) || "MP"}
                      </Avatar>
                      <Typography variant="body2">
                        {mp.user?.full_name || `MP-${mp.id}`}
                      </Typography>
                      {mp.has_starting_point ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Optimized"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<WarningIcon />}
                          label="No Starting Point"
                          color="warning"
                          size="small"
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      MP-{route.medical_professional_id}
                    </Typography>
                  );
                })()}
              </Box>
            )}

            {/* Add Order Button */}
            <Box mt={3}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                fullWidth
                onClick={handleOpenAddDialog}
                disabled={saving}
              >
                Add Order to Route
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid  size={{ xs: 12, md: 8 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Orders in Route</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="textSecondary">
                  {orders.length} order{orders.length !== 1 ? "s" : ""}
                </Typography>
                {/* Show if route is optimized */}
                {route.medical_professional_id &&
                  (() => {
                    const mp = medicalProfessionals.find(
                      (mp) => mp.id === route.medical_professional_id
                    );
                    return (
                      mp?.has_starting_point && (
                        <Chip
                          icon={<OptimizedIcon />}
                          label="Optimized Route"
                          color="success"
                          size="small"
                        />
                      )
                    );
                  })()}
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Optimization Alert */}
            {route.medical_professional_id &&
              (() => {
                const mp = medicalProfessionals.find(
                  (mp) => mp.id === route.medical_professional_id
                );
                return (
                  mp &&
                  !mp.has_starting_point &&
                  orders.length > 0 && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        🎯 Route Optimization Available
                      </Typography>
                      <Typography variant="body2">
                        Configure a starting point for{" "}
                        {mp.user?.full_name || `MP-${mp.id}`} to enable
                        automatic route optimization and better travel sequence.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                        onClick={() =>
                          navigate(`/medical-professionals/${mp.id}/edit`)
                        }
                      >
                        Configure Starting Point
                      </Button>
                    </Alert>
                  )
                );
              })()}

            <Alert severity="info" sx={{ mb: 3 }}>
              Drag and drop orders to reorder them or use the arrow buttons.
              Orders will be visited in the sequence shown below.
              {route.medical_professional_id &&
                (() => {
                  const mp = medicalProfessionals.find(
                    (mp) => mp.id === route.medical_professional_id
                  );
                  return (
                    mp?.has_starting_point && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        ✅ This route uses the medical professional's starting
                        point for optimization.
                      </Typography>
                    )
                  );
                })()}
            </Alert>

            {orders.length === 0 ? (
              <Box textAlign="center" py={5}>
                <Typography color="textSecondary">
                  No orders in this route. Add some orders to get started.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                  onClick={handleOpenAddDialog}
                >
                  Add Orders
                </Button>
              </Box>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="orders">
                  {(provided) => (
                    <List
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{ width: "100%" }}
                    >
                      {orders.map((order, index) => (
                        <Draggable
                          key={order.id}
                          draggableId={order.id.toString()}
                          index={index}
                        >
                          {(provided) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                mb: 1,
                                border: "1px solid #eee",
                                borderRadius: 1,
                                position: "relative",
                                "&:hover": {
                                  backgroundColor: "#f9f9f9",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: 12,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  width: 28,
                                  height: 28,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "50%",
                                  backgroundColor: "primary.main",
                                  color: "white",
                                  fontWeight: "bold",
                                }}
                              >
                                {index + 1}
                              </Box>

                              <ListItemText
                                sx={{ ml: 4 }}
                                primary={
                                  <Box display="flex" alignItems="center">
                                    <Typography variant="subtitle1">
                                      {order.order_number}
                                    </Typography>
                                    {order.status && (
                                      <Chip
                                        label={order.status.replace("_", " ")}
                                        size="small"
                                        sx={{ ml: 1 }}
                                        color={
                                          order.status === "completed"
                                            ? "success"
                                            : order.status === "in_progress"
                                            ? "primary"
                                            : order.status === "assigned"
                                            ? "info"
                                            : "warning"
                                        }
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <>
                                    <Typography
                                      component="span"
                                      variant="body2"
                                    >
                                      {order.patient_name || "No patient name"}
                                    </Typography>
                                    <Typography
                                      component="div"
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {order.patient_address || "No address"}
                                    </Typography>
                                  </>
                                }
                              />

                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveOrder(index, -1)}
                                  disabled={index === 0}
                                >
                                  <MoveUpIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveOrder(index, 1)}
                                  disabled={index === orders.length - 1}
                                >
                                  <MoveDownIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveOrder(order.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add Order Dialog */}
      <Dialog
        open={openAddOrderDialog}
        onClose={() => setOpenAddOrderDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Add Order to Route
          {route.medical_professional_id &&
            (() => {
              const mp = medicalProfessionals.find(
                (mp) => mp.id === route.medical_professional_id
              );
              return (
                mp?.has_starting_point && (
                  <Chip
                    icon={<OptimizedIcon />}
                    label="Will be optimized"
                    color="success"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                )
              );
            })()}
        </DialogTitle>
        <DialogContent>
          <Box mb={3} mt={1}>
            <TextField
              fullWidth
              label="Search Orders"
              value={orderSearchQuery}
              onChange={(e) => setOrderSearchQuery(e.target.value)}
              placeholder="Search by order number, patient name, or address"
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                ),
              }}
            />
          </Box>

          {/* 🔥 NEW: Date filter info */}
          {orderDateFilter && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Showing orders for: {formatDate(orderDateFilter)}
              {scheduleDate !== route.scheduled_date && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  💡 Orders are filtered by the new schedule date you selected
                </Typography>
              )}
            </Alert>
          )}

          {availableOrders.length === 0 ? (
            <Alert severity="info">
              No available orders found for{" "}
              {orderDateFilter ? formatDate(orderDateFilter) : "this date"}.
              Create new orders with this date to add them to the route.
            </Alert>
          ) : (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Select an order to add:
                {route.medical_professional_id &&
                  (() => {
                    const mp = medicalProfessionals.find(
                      (mp) => mp.id === route.medical_professional_id
                    );
                    return (
                      mp?.has_starting_point && (
                        <Typography
                          variant="caption"
                          color="success.main"
                          display="block"
                        >
                          Orders will be automatically optimized based on
                          medical professional's starting point
                        </Typography>
                      )
                    );
                  })()}
              </Typography>

              <Autocomplete
                value={selectedOrder}
                onChange={(event, newValue) => {
                  setSelectedOrder(newValue);
                }}
                options={filteredAvailableOrders}
                getOptionLabel={(option) =>
                  `${option.order_number} - ${
                    option.patient?.first_name || ""
                  } ${option.patient?.last_name || ""}`
                }
                renderInput={(params) => (
                  <TextField {...params} label="Select Order" />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1">
                        {option.order_number}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.patient?.first_name} {option.patient?.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.patient?.address}, {option.patient?.city},{" "}
                        {option.patient?.state} {option.patient?.zip_code}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddOrderDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddOrder}
            disabled={!selectedOrder}
          >
            Add to Route
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RouteEdit;
